/*
 * SNUGLER redesign: numbers, videos, features, order timeline, FAQ and final call.
 * Needs snugler-core.js, which must load first.
 */
(() => {
  const SR = window.SnuglerRedesign;
  if (!SR || !SR.register || SR.modules.has('sections')) return;
  const { isReduced, finePointer, clamp, range, lerp, smooth, easeOut, easeBack, fmt, onScroll, whenVisible, store, selectVariant } = SR.util;

  /* ------------------------------------------------------------------ stats */

  function initStats(root) {
    root.querySelectorAll('[data-snx-count]').forEach((el) => {
      if (el.__snx) return;
      el.__snx = true;
      const to = parseFloat(el.dataset.snxCount);
      const from = parseFloat(el.dataset.snxCountFrom || '0');
      if (isNaN(to) || isReduced() || from === to) return;
      const decimals = (String(el.dataset.snxCount).split('.')[1] || '').length;
      el.textContent = from.toFixed(decimals);
      whenVisible(
        el,
        () => {
          const start = performance.now();
          const dur = 1400 + Math.min(900, Math.abs(to - from) * 2);
          const tick = (now) => {
            const t = clamp((now - start) / dur);
            el.textContent = lerp(from, to, easeOut(t)).toFixed(decimals);
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        },
        { threshold: 0.6 }
      );
    });
  }

  /* ------------------------------------------------------------------ videos */

  const reels = new Set();

  function pauseOthers(except) {
    reels.forEach((reel) => {
      if (reel !== except && !reel.video.paused) reel.video.pause();
    });
  }

  function initVideos(root) {
    const section = root.querySelector('[data-snx-videos]');
    if (!section || section.__snx) return;
    section.__snx = true;

    const rail = section.querySelector('[data-snx-rail]');
    section.querySelectorAll('[data-snx-reel]').forEach((figure) => {
      const video = figure.querySelector('video');
      const toggle = figure.querySelector('[data-snx-reel-toggle]');
      const frame = figure.querySelector('.snx-reel__frame');
      const reel = { figure, video };
      reels.add(reel);
      const label = toggle.getAttribute('aria-label') || 'Play video';
      let hideTimer = 0;

      const play = () => {
        pauseOthers(reel);
        const promise = video.play();
        if (promise && promise.catch) promise.catch(() => {});
      };
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        if (video.paused) play();
        else video.pause();
      });
      video.addEventListener('click', () => {
        if (video.paused) play();
        else {
          figure.classList.add('show-controls');
          clearTimeout(hideTimer);
          hideTimer = setTimeout(() => figure.classList.remove('show-controls'), 1400);
        }
      });
      video.addEventListener('play', () => {
        pauseOthers(reel);
        figure.classList.add('is-playing');
        toggle.setAttribute('aria-label', label.replace(/^Play/, 'Pause'));
      });
      video.addEventListener('pause', () => {
        figure.classList.remove('is-playing');
        toggle.setAttribute('aria-label', label);
      });
      video.addEventListener('ended', () => {
        figure.classList.remove('is-playing');
        toggle.style.setProperty('--progress', '0');
      });
      video.addEventListener('timeupdate', () => {
        if (video.duration) toggle.style.setProperty('--progress', String(video.currentTime / video.duration));
      });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting && !video.paused) video.pause();
            });
          },
          { threshold: 0.35 }
        ).observe(video);
      }

      // tilt toward the cursor
      frame.addEventListener('pointermove', (e) => {
        if (isReduced() || !finePointer.matches) return;
        const rect = frame.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        frame.style.setProperty('--ry', `${fmt(x * 10)}deg`);
        frame.style.setProperty('--rx', `${fmt(y * -8)}deg`);
      });
      frame.addEventListener('pointerleave', () => {
        frame.style.setProperty('--ry', '0deg');
        frame.style.setProperty('--rx', '0deg');
      });
    });

    const step = () => {
      const card = rail.querySelector('.snx-reel');
      return card ? card.getBoundingClientRect().width + 20 : 300;
    };
    const prev = section.querySelector('[data-snx-rail-prev]');
    const next = section.querySelector('[data-snx-rail-next]');
    const behavior = () => (isReduced() ? 'auto' : 'smooth');
    if (prev) prev.addEventListener('click', () => rail.scrollBy({ left: -step() * 2, behavior: behavior() }));
    if (next) next.addEventListener('click', () => rail.scrollBy({ left: step() * 2, behavior: behavior() }));
  }

  /* ------------------------------------------------------------------ chapters + dawn */

  function initChapters(root) {
    const section = root.querySelector('[data-snx-chapters]');
    if (!section || section.__snx) return;
    section.__snx = true;
    if (isReduced()) {
      section.querySelectorAll('[data-snx-clip]').forEach((m) => m.style.setProperty('--clip', '0'));
      const dawn = section.querySelector('[data-snx-dawn]');
      if (dawn) dawn.style.setProperty('--rise', '1');
      return;
    }

    section.querySelectorAll('[data-snx-clip]').forEach((media) => {
      const img = media.querySelector('[data-snx-parallax]');
      onScroll(media, (vh) => {
        const rect = media.getBoundingClientRect();
        const reveal = easeOut(range(vh - rect.top, vh * 0.05, vh * 0.75));
        media.style.setProperty('--clip', String(fmt((1 - reveal) * 1000) / 1000));
        const center = rect.top + rect.height / 2 - vh / 2;
        if (img) img.style.setProperty('--shift', `${fmt(center * -0.06)}px`);
      });
    });

    const dawn = section.querySelector('[data-snx-dawn]');
    if (dawn) {
      onScroll(dawn, (vh) => {
        const rect = dawn.getBoundingClientRect();
        const rise = range(vh - rect.top, vh * 0.1, vh * 0.85);
        dawn.style.setProperty('--rise', String(fmt(easeOut(rise) * 1000) / 1000));
      });
    }
  }

  /* ------------------------------------------------------------------ promise timeline */

  function initPromise(root) {
    const section = root.querySelector('[data-snx-promise]');
    if (!section || section.__snx) return;
    section.__snx = true;
    const steps = section.querySelector('[data-snx-steps]');
    const items = Array.from(section.querySelectorAll('[data-snx-step]'));
    if (!steps) return;
    if (isReduced()) {
      steps.style.setProperty('--progress', '1');
      items.forEach((i) => i.classList.add('is-on'));
      return;
    }
    onScroll(steps, (vh) => {
      const rect = steps.getBoundingClientRect();
      const p = range(vh * 0.78 - rect.top, 0, rect.height + vh * 0.12);
      steps.style.setProperty('--progress', String(fmt(p * 1000) / 1000));
      items.forEach((item, i) => {
        const threshold = items.length > 1 ? i / (items.length - 1) : 0;
        item.classList.toggle('is-on', p >= threshold * 0.92);
      });
    });
  }

  /* ------------------------------------------------------------------ FAQ */

  function initFaq(root) {
    root.querySelectorAll('[data-snx-qa]').forEach((details) => {
      if (details.__snx) return;
      details.__snx = true;
      const summary = details.querySelector('summary');
      const panel = details.querySelector('.snx-qa__a');
      summary.addEventListener('click', (event) => {
        if (isReduced() || !panel.animate) return;
        event.preventDefault();
        if (details.__anim) details.__anim.cancel();
        if (details.open) {
          const height = panel.offsetHeight;
          details.classList.add('is-closing');
          details.__anim = panel.animate([{ height: `${height}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 380, easing: 'cubic-bezier(.65,0,.35,1)' });
          details.__anim.onfinish = () => {
            details.open = false;
            details.classList.remove('is-closing');
            details.__anim = null;
          };
        } else {
          details.open = true;
          const height = panel.offsetHeight;
          details.__anim = panel.animate([{ height: '0px', opacity: 0 }, { height: `${height}px`, opacity: 1 }], { duration: 520, easing: 'cubic-bezier(.22,1,.36,1)' });
          details.__anim.onfinish = () => (details.__anim = null);
        }
      });
    });
  }

  /* ------------------------------------------------------------------ CTA sunrise */

  function initCta(root) {
    const section = root.querySelector('[data-snx-cta]');
    if (!section || section.__snx) return;
    section.__snx = true;
    if (isReduced()) {
      section.style.setProperty('--rise', '1');
      return;
    }
    onScroll(section, (vh) => {
      const rect = section.getBoundingClientRect();
      const rise = easeOut(range(vh - rect.top, vh * 0.05, vh * 0.9));
      section.style.setProperty('--rise', String(fmt(rise * 1000) / 1000));
      section.classList.toggle('is-visible', rect.top < vh && rect.bottom > 0);
    });
  }

  /* ------------------------------------------------------------------ counts for grid columns */

  function initCounts(root) {
    root.querySelectorAll('.snx-stats__list').forEach((list) => list.style.setProperty('--snx-stat-count', String(list.children.length)));
    root.querySelectorAll('.snx-steps__list').forEach((list) => list.style.setProperty('--snx-step-count', String(list.children.length)));
  }

  SR.register('sections', (root) => {
    initCounts(root);
    initStats(root);
    initVideos(root);
    initChapters(root);
    initPromise(root);
    initFaq(root);
    initCta(root);
  });
})();